import LegalPage from "@/components/legal/LegalPage";

const sections = [
  {
    id: "who-we-are",
    title: "Who we are",
    body: (
      <>
        <p>
          This privacy policy explains how Toulios Wealth Management Pty Ltd
          (ACN 667 133 977 · ABN 68 667 133 977), an Australian private company (&ldquo;Toulios&rdquo;,
          &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;), collects,
          uses, discloses and stores personal information.
        </p>
        <p>
          We are bound by the Australian Privacy Principles (APPs) under the
          Privacy Act 1988 (Cth). This policy applies to our website, our
          consulting engagements, and any other interactions you have with us.
        </p>
      </>
    ),
  },
  {
    id: "what-we-collect",
    title: "Information we collect",
    body: (
      <>
        <p>We only collect personal information that is reasonably necessary for our consulting services. Typical categories include:</p>
        <ul className="list-disc list-outside space-y-2 pl-5 marker:text-gold/70">
          <li>Identification details: name, role, employer.</li>
          <li>Contact details: email address, phone number, postal address.</li>
          <li>Engagement context: company stage, the brief you share with us, your responses to the AI Readiness Scorecard.</li>
          <li>Technical data: IP address, device/browser type, pages visited (via standard server logs and analytics).</li>
          <li>Communications: emails, call notes, materials you share during an engagement.</li>
        </ul>
        <p>
          We do not knowingly collect sensitive information (as defined in the
          Privacy Act) unless it is required for a specific engagement and you
          have given consent.
        </p>
      </>
    ),
  },
  {
    id: "how-we-use-it",
    title: "How we use your information",
    body: (
      <>
        <p>We use personal information to:</p>
        <ul className="list-disc list-outside space-y-2 pl-5 marker:text-gold/70">
          <li>Respond to your enquiry and arrange a strategy call.</li>
          <li>Deliver consulting and advisory services we have been engaged to provide.</li>
          <li>Send tailored follow-up relating to the topic of your enquiry.</li>
          <li>Improve our website, content, tools (including the AI Readiness Scorecard) and service quality.</li>
          <li>Meet our legal, regulatory, accounting and record-keeping obligations.</li>
        </ul>
        <p>
          We do not sell your personal information. We do not use your data to
          train third-party AI models. We do not engage in profiling or
          automated decision-making that produces legal or similarly significant
          effects.
        </p>
      </>
    ),
  },
  {
    id: "lawful-basis",
    title: "Lawful basis & consent",
    body: (
      <>
        <p>We collect and process personal information on the following bases:</p>
        <ul className="list-disc list-outside space-y-2 pl-5 marker:text-gold/70">
          <li><strong className="text-white font-medium">Consent</strong>, when you fill in a form, run the scorecard, or otherwise voluntarily provide information.</li>
          <li><strong className="text-white font-medium">Contract</strong>, to deliver services you have engaged us to perform.</li>
          <li><strong className="text-white font-medium">Legitimate interests</strong>, to operate our website and respond to enquiries in a responsive, professional manner.</li>
          <li><strong className="text-white font-medium">Legal obligation</strong>, to comply with applicable laws and regulatory requirements.</li>
        </ul>
      </>
    ),
  },
  {
    id: "sharing",
    title: "Who we share information with",
    body: (
      <>
        <p>
          We treat client information with the utmost discretion. We share
          personal information only with:
        </p>
        <ul className="list-disc list-outside space-y-2 pl-5 marker:text-gold/70">
          <li>Trusted service providers under contract (hosting, email delivery, analytics) bound by confidentiality and data-processing obligations.</li>
          <li>Professional advisers (accountants, lawyers) where strictly necessary.</li>
          <li>Authorities, where required by law or to protect our or a third party&rsquo;s rights.</li>
        </ul>
        <p>
          We do not disclose engagement details to other clients, prospects or
          third parties. Mutual NDAs are available on request before
          substantive discussions.
        </p>
      </>
    ),
  },
  {
    id: "overseas",
    title: "Overseas storage and transfers",
    body: (
      <>
        <p>
          Some of our service providers (e.g. cloud hosting, email infrastructure
          and analytics) may store and process data in countries outside
          Australia, including the United States and the European Union. When
          this occurs, we take reasonable steps to ensure recipients handle
          your information consistent with the APPs and applicable laws.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "Data retention",
    body: (
      <>
        <p>
          We retain personal information only for as long as is reasonably
          necessary to deliver our services, comply with applicable laws, or
          maintain a record of our business activities. Enquiry and scorecard
          submissions are typically retained for up to seven (7) years from the
          date of last interaction, after which they are deleted or anonymised.
        </p>
      </>
    ),
  },
  {
    id: "security",
    title: "Security",
    body: (
      <>
        <p>
          We apply reasonable physical, technical and administrative safeguards
          to protect personal information against loss, misuse, unauthorised
          access, modification or disclosure. These include access controls,
          encryption in transit (TLS), reputable cloud providers, and
          confidentiality obligations for our team and service providers.
        </p>
        <p>
          No system is perfectly secure. If we become aware of a notifiable
          data breach affecting you, we will comply with the Notifiable Data
          Breaches scheme under the Privacy Act.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies & analytics",
    body: (
      <>
        <p>
          Our website may use first-party and third-party cookies for essential
          functionality and aggregated analytics. Cookies do not personally
          identify you. You can disable cookies in your browser; some parts of
          the site may not function as intended if you do so.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "Your rights",
    body: (
      <>
        <p>Under the Privacy Act and APPs, you have the right to:</p>
        <ul className="list-disc list-outside space-y-2 pl-5 marker:text-gold/70">
          <li>Request access to the personal information we hold about you.</li>
          <li>Request correction of inaccurate or out-of-date information.</li>
          <li>Withdraw consent for marketing communications at any time.</li>
          <li>Request deletion of your personal information where we are not legally required to retain it.</li>
          <li>Make a complaint about how we handle your personal information.</li>
        </ul>
        <p>
          To exercise any of these rights, contact us using the details below.
          We will respond within 30 days.
        </p>
      </>
    ),
  },
  {
    id: "complaints",
    title: "Complaints",
    body: (
      <>
        <p>
          If you are not satisfied with how we have handled your personal
          information, you may lodge a complaint with the Office of the
          Australian Information Commissioner (OAIC) at{" "}
          <a className="text-gold hover:underline" href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer">oaic.gov.au</a>.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: (
      <>
        <p>
          We may update this privacy policy from time to time to reflect changes
          in our practices or in applicable law. The &ldquo;Last updated&rdquo;
          date at the top of this page will always reflect the most recent
          revision.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "How to contact us",
    body: (
      <>
        <p>
          For any privacy enquiry, please reach out via the{" "}
          <a className="text-gold hover:underline" href="/contact">Contact</a>{" "}
          page, or write to us at{" "}
          <span className="text-white">privacy@touliosWealth.com</span>.
        </p>
        <p className="text-zinc-500 text-sm">
          Toulios Wealth Management Pty Ltd · Sydney · Australia.
        </p>
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <LegalPage
      eyebrow="Privacy"
      title="Privacy Policy."
      lastUpdated="20 May 2026"
      summary="A plain-English explanation of what personal information we collect, why we collect it, how we use it, and the rights you have under Australian law."
      sections={sections}
    />
  );
}
